import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Trash2, CheckCircle2, Clock, RefreshCw, Calendar } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { listAnomalies, updateAnomaly, deleteAnomaly } from "@/lib/api";
import JustifyRecordsSection from "@/components/anomalies/JustifyRecordsSection";

const SEVERITY_COLORS = {
  low:    { bg: "bg-emerald-100 dark:bg-emerald-950/40", text: "text-emerald-700 dark:text-emerald-300", border: "border-emerald-200 dark:border-emerald-900", label: "Baixa" },
  medium: { bg: "bg-amber-100   dark:bg-amber-950/40",   text: "text-amber-700   dark:text-amber-300",   border: "border-amber-200   dark:border-amber-900",   label: "Média" },
  high:   { bg: "bg-red-100     dark:bg-red-950/40",     text: "text-red-700     dark:text-red-300",     border: "border-red-200     dark:border-red-900",     label: "Alta" },
};

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso.slice(11, 16);
  }
}

function fmtDurationMin(min) {
  if (min === null || min === undefined) return "—";
  const n = Number(min);
  if (Number.isNaN(n)) return "—";
  if (n < 60) return `${Math.round(n)} min`;
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  return `${h}h${String(m).padStart(2, "0")}`;
}

function durationMin(a) {
  if (!a?.start_at) return 0;
  const start = new Date(a.start_at).getTime();
  const end = a.end_at ? new Date(a.end_at).getTime() : Date.now();
  return Math.max(0, (end - start) / 60000);
}

export default function AnomalyReporterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDate = searchParams.get("date") || todayIso();
  const [date, setDate] = useState(initialDate);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Keep URL in sync when the user changes the date picker
  useEffect(() => {
    const current = searchParams.get("date") || "";
    if (date !== current) {
      const next = new URLSearchParams(searchParams);
      next.set("date", date);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Imperative refresh of the records-with-justifications section
  const justifyRef = useRef(null);

  async function refresh() {
    setLoading(true);
    try {
      const data = await listAnomalies({ date });
      setList(data || []);
    } catch (e) {
      toast.error(`Falha ao carregar anomalias: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }

  function refreshAll() {
    refresh();
    justifyRef.current?.refresh?.();
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Auto-refresh every 30s so open anomalies show live duration
  useEffect(() => {
    const id = setInterval(() => setList((prev) => [...prev]), 30000);
    return () => clearInterval(id);
  }, []);

  async function closeAnomaly(a) {
    try {
      await updateAnomaly(a.id, { close: true });
      toast.success("Anomalia fechada");
      refreshAll();
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function removeAnomaly(a) {
    if (!confirm(`Apagar a anomalia "${a.reason}"?`)) return;
    try {
      await deleteAnomaly(a.id);
      toast.success("Anomalia apagada");
      refreshAll();
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    }
  }

  const stats = useMemo(() => {
    const open = list.filter((a) => !a.end_at).length;
    const closed = list.filter((a) => a.end_at).length;
    const totalMin = list.reduce((acc, a) => acc + durationMin(a), 0);
    return { open, closed, totalMin: Math.round(totalMin) };
  }, [list]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto" data-testid="anomaly-reporter-page">
      {/* Page header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-amber-500/15 text-amber-600 grid place-items-center shrink-0">
            <AlertTriangle className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Reporte de Anomalias</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Abre <span className="font-medium text-foreground">Ver linhas</span>, clica numa linha para justificar / reportar anomalia. Tudo é refletido no toggle <span className="font-medium text-foreground">Real / Ajustado</span> do painel.
            </p>
          </div>
        </div>

        {/* Big day picker — primary control */}
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:justify-between">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" /> Dia em análise
            </label>
            <input
              type="date"
              value={date}
              max={todayIso()}
              onChange={(e) => setDate(e.target.value)}
              data-testid="anomaly-date-input"
              className="bg-card border-2 border-border rounded-lg px-4 py-3 text-lg font-semibold tabular-nums text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary w-fit"
            />
          </div>
          <Button size="sm" variant="outline" onClick={refreshAll} disabled={loading} data-testid="anomaly-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Atualizar
          </Button>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <Card className="p-3 sm:p-4" data-testid="anomaly-stat-open">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Em curso</div>
          <div className="text-2xl sm:text-3xl font-semibold mt-1 text-amber-600">{stats.open}</div>
        </Card>
        <Card className="p-3 sm:p-4" data-testid="anomaly-stat-closed">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Fechadas</div>
          <div className="text-2xl sm:text-3xl font-semibold mt-1 text-emerald-600">{stats.closed}</div>
        </Card>
        <Card className="p-3 sm:p-4" data-testid="anomaly-stat-total">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Tempo total</div>
          <div className="text-2xl sm:text-3xl font-semibold mt-1">{stats.totalMin} min</div>
        </Card>
      </div>

      {/* Day records — primary action surface for the operator */}
      <JustifyRecordsSection ref={justifyRef} date={date} onChanged={refresh} />

      {/* Anomalies of the day — read-only list */}
      <Card className="p-4 sm:p-5" data-testid="anomaly-list">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-foreground">
            Anomalias de <span className="tabular-nums">{date}</span>
            {list.length > 0 && (
              <span className="text-muted-foreground font-normal ml-1">({list.length})</span>
            )}
          </h2>
        </div>
        {list.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center border-2 border-dashed border-border rounded-lg">
            Sem anomalias registadas neste dia.
            <div className="text-[11px] text-muted-foreground/80 mt-1">
              Abre <span className="font-medium">Ver linhas</span> acima e clica numa linha para registar.
            </div>
          </div>
        ) : (
          <ul className="space-y-2">
            {list.map((a) => {
              const sev = SEVERITY_COLORS[a.severity] || SEVERITY_COLORS.medium;
              const isOpen = !a.end_at;
              return (
                <li
                  key={a.id}
                  className={`rounded-lg border ${sev.border} ${sev.bg} px-3 py-2 flex items-center gap-3`}
                  data-testid={`anomaly-${a.id}`}
                >
                  <Badge className={`${sev.bg} ${sev.text} ${sev.border} font-medium`}>
                    {sev.label}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-foreground truncate">{a.reason}</div>
                    <div className="text-[11px] text-muted-foreground tabular-nums flex items-center gap-2 flex-wrap">
                      <span><Clock className="h-3 w-3 inline mr-0.5" />{fmtTime(a.start_at)} → {a.end_at ? fmtTime(a.end_at) : <span className="text-amber-600 font-semibold">em curso</span>}</span>
                      <span>· {fmtDurationMin(durationMin(a))}</span>
                      {a.operator && <span className="hidden sm:inline">· {a.operator}</span>}
                    </div>
                    {a.description && (
                      <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{a.description}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isOpen && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => closeAnomaly(a)}
                        data-testid={`anomaly-close-${a.id}`}
                        title="Fechar agora"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1 text-emerald-600" />
                        Fechar
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeAnomaly(a)}
                      data-testid={`anomaly-remove-${a.id}`}
                      className="text-destructive hover:text-destructive"
                      title="Apagar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
